import {usePopup} from "@/components/layouts/popup/InfoPopup/PopupContext";

import {useCallback, useEffect, useRef, useState} from "react";

import {isEqual} from "@helpers";
import activityImg from "@images/general/activity.png";
import femaleImg from "@images/general/avatar_female.png";
import maleImg from "@images/general/avatar_male.png";
import expirienceImg from "@images/general/expirience.png";
import infoImg from "@images/general/info.png";
import intensityImg from "@images/general/intensivity.png";
import medicalTypeImg from "@images/general/med-type.png";
import prioritiesImg from "@images/general/priority.png";
import specialityImg from "@images/general/speciality.png";
import {FillDataPopup} from "@popups/attentionPopups/FillDataPopup";
import {SuccessfulSavePopup} from "@popups/attentionPopups/SuccessfulSavePopup";
import {
  DOCTOR_ENUMS,
  clearCreateDoctor,
  initialCreateDoctor,
  postCreateDoctor,
  setCreateDoctor,
  setError,
  setIsChanged,
  setIsDoctorCreate,
} from "@redux/slices/doctorSlices/createSlice";
import {viewDoctor as fetchViewDoctor} from "@redux/slices/doctorSlices/viewSlice";
import {BackButton} from "@ui/buttons/BackButton";
import {ControlButton} from "@ui/buttons/ControlButton";
import {RootDesc} from "@ui/descriptions/RootDesc";
import {SmallDesc} from "@ui/descriptions/SmallDesc";
import {EditField} from "@ui/forms/EditField";
import {BorderArticle} from "@ui/general/BorderArticle";
import {Spinner} from "@ui/general/Spinner";
import {EditInput} from "@ui/inputs/EditInput";
import {RadioGroup} from "@ui/inputs/RootRadio";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import styles from "./styles.module.scss";

export const DoctorDescField = ({label, value, icon}) => {
  const cleanLabel = label?.replace(/\*/g, "") || "";

  return (
    <li key={value}>
      <div className={styles.doctorDescFieldIcon}>
        <img
          src={icon}
          alt={"icon"}
        />
      </div>

      <div className={styles.doctorDescFieldValue}>
        <SmallDesc>
          <b dangerouslySetInnerHTML={{__html: cleanLabel}} />
        </SmallDesc>

        <SmallDesc>
          <span dangerouslySetInnerHTML={{__html: value || "-"}} />
        </SmallDesc>
      </div>
    </li>
  );
};

const getDoctorPreviewOptions = (t) => ({
  mainType: [
    {label: t("page.doctor.radio.main_type.analyst.title"), value: "analyst"},
    {
      label: t("page.doctor.radio.main_type.ambitious.title"),
      value: "ambitious",
    },
    {label: t("page.doctor.radio.main_type.caring.title"), value: "caring"},
    {label: t("page.doctor.radio.main_type.simple.title"), value: "simple"},
  ],
  experience: [
    {label: t("page.doctor.radio.experience.new"), value: "new"},
    {
      label: t("page.doctor.radio.experience.experienced"),
      value: "experienced",
    },
    {label: t("page.doctor.radio.experience.expert"), value: "expert"},
  ],
  medicalType: [
    {
      name: t("page.doctor.radio.medical_type.municipal_polyclinic"),
      type: "municipal_polyclinic",
    },
    {
      name: t("page.doctor.radio.medical_type.commercial_polyclinic"),
      type: "commercial_polyclinic",
    },
    {
      name: t("page.doctor.radio.medical_type.municipal_hospital"),
      type: "municipal_hospital",
    },
    {
      name: t("page.doctor.radio.medical_type.commercial_hospital"),
      type: "commercial_hospital",
    },
  ],
  priorities: [
    {label: t("page.doctor.radio.priorities.career"), value: "career"},
    {label: t("page.doctor.radio.priorities.balanced"), value: "balanced"},
    {label: t("page.doctor.radio.priorities.community"), value: "community"},
  ],
  intensity: [
    {label: t("page.doctor.radio.intensity.low"), value: "low"},
    {label: t("page.doctor.radio.intensity.medium"), value: "medium"},
    {label: t("page.doctor.radio.intensity.high"), value: "high"},
  ],
  activity: [
    {label: t("page.doctor.radio.activity.active"), value: "active"},
    {label: t("page.doctor.radio.activity.inactive"), value: "inactive"},
  ],
  speciality: DOCTOR_ENUMS.speciality.map((value) => ({
    name: t(`page.doctor.radio.speciality.${value}`),
    value,
  })),
});

export const DoctorPreviewContent = ({value}) => {
  const {t} = useTranslation();
  const opts = getDoctorPreviewOptions(t);
  if (!value) return null;
  return (
    <BorderArticle>
      <div className={styles.doctorPreviewCover}>
        {value?.gender_type === "male" && (
          <img
            src={maleImg}
            alt={"male"}
          />
        )}
        {value?.gender_type === "female" && (
          <img
            src={femaleImg}
            alt={"female"}
          />
        )}
        <div>
          <h4>{value?.name}</h4>
          <RootDesc>
            <span>
              {opts.mainType.find((o) => o.value === value?.main_type)?.label}
            </span>
          </RootDesc>
        </div>
      </div>
      <ul>
        <DoctorDescField
          icon={expirienceImg}
          label={t("page.doctor.field.experience")}
          value={
            opts.experience.find((o) => o.value === value?.experience)?.label
          }
        />
        <DoctorDescField
          icon={medicalTypeImg}
          label={t("page.doctor.field.medical_type")}
          value={
            opts.medicalType.find((o) => o.type === value?.medical_type)?.name
          }
        />
        <DoctorDescField
          icon={prioritiesImg}
          label={t("page.doctor.field.priorities")}
          value={
            opts.priorities.find((o) => o.value === value?.priorities)?.label
          }
        />
        <DoctorDescField
          icon={intensityImg}
          label={t("page.doctor.field.intensity")}
          value={
            opts.intensity.find((o) => o.value === value?.intensity)?.label
          }
        />
        <DoctorDescField
          icon={activityImg}
          label={t("page.doctor.field.activity")}
          value={opts.activity.find((o) => o.value === value?.activity)?.label}
        />
        <DoctorDescField
          icon={specialityImg}
          label={t("page.doctor.field.speciality")}
          value={
            opts.speciality.find((o) => o.value === value?.speciality)?.name
          }
        />
      </ul>
    </BorderArticle>
  );
};

const DoctorInfoPopupContent = ({doctorId}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {doctor, serverStatus} = useSelector((state) => state.doctor.view);

  useEffect(() => {
    if (doctorId) dispatch(fetchViewDoctor({id: doctorId}));
  }, [doctorId, dispatch]);

  return (
    <div className={styles.doctorPreviewContent}>
      {serverStatus === "loading" && (
        <BorderArticle>
          <div className={styles.doctorPreviewLoading}>
            <Spinner
              width={60}
              height={60}
            />
          </div>
        </BorderArticle>
      )}
      {(serverStatus === "error" ||
        (serverStatus === "success" && !doctor)) && (
        <BorderArticle>
          <SmallDesc>{t("page.doctor.error_load")}</SmallDesc>
        </BorderArticle>
      )}
      {serverStatus === "success" && doctor && (
        <DoctorPreviewContent value={doctor} />
      )}
    </div>
  );
};

export const DoctorField = ({value, doctorProfileId}) => {
  const [open, setOpen] = useState(false);
  const doctorRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        open &&
        doctorRef.current &&
        !doctorRef.current.contains(event.target)
      )
        setOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [open]);

  const handleIconClick = () => {
    if (doctorProfileId != null || value != null) setOpen((o) => !o);
  };

  const showInline = value != null && doctorProfileId === undefined;
  const showByDoctorId = doctorProfileId != null && doctorProfileId !== "";
  const isPopupMode = doctorProfileId !== undefined;
  const hideIcon = isPopupMode && !showByDoctorId;
  const isOpen = open && (showInline || showByDoctorId);

  return (
    <div
      className={styles.doctorPreview}
      ref={doctorRef}
    >
      {!hideIcon && (
        <i
          onClick={handleIconClick}
          style={{cursor: "pointer"}}
          role={"button"}
          aria-label={"info"}
        >
          <img
            src={infoImg}
            alt={"info"}
          />
        </i>
      )}
      {isOpen && (
        <div className={styles.doctorPreviewBlock}>
          {showInline ? (
            <div className={styles.doctorPreviewContent}>
              <DoctorPreviewContent value={value} />
            </div>
          ) : (
            <DoctorInfoPopupContent doctorId={doctorProfileId} />
          )}
        </div>
      )}
    </div>
  );
};

const DoctorContentMainType = ({value}) => {
  const {t} = useTranslation();

  const [text, setText] = useState(value);

  useEffect(() => {
    switch (value) {
      case "analyst":
        setText(t("page.doctor.radio.main_type.analyst.desc"));
        break;
      case "ambitious":
        setText(t("page.doctor.radio.main_type.ambitious.desc"));
        break;
      case "caring":
        setText(t("page.doctor.radio.main_type.caring.desc"));
        break;
      case "simple":
        setText(t("page.doctor.radio.main_type.simple.desc"));
        break;
    }
  }, [value]);

  return (
    <div className={styles.doctorContentMainTypeDesc}>
      <BorderArticle>
        <SmallDesc>
          <span dangerouslySetInnerHTML={{__html: text}} />
        </SmallDesc>
      </BorderArticle>
    </div>
  );
};

export const DoctorCreatePage = () => {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {openPopup} = usePopup();

  const {isMobile} = useSelector((state) => state.common.settings);
  const {createDoctor, serverStatus} = useSelector(
    (state) => state.doctor.create,
  );

  const doctorRef = useRef();

  const updateDoctorField = useCallback(
    (fieldType, value) => {
      dispatch(setIsChanged(true));
      dispatch(
        setCreateDoctor({
          ...createDoctor,
          [fieldType]: value,
        }),
      );
    },
    [createDoctor, dispatch],
  );

  const handleClickSave = async () => {
    if (
      !createDoctor.name ||
      !createDoctor.avatar_type ||
      !createDoctor.gender_type ||
      !createDoctor.main_type ||
      !createDoctor.experience ||
      !createDoctor.medical_type ||
      !createDoctor.priorities ||
      !createDoctor.intensity ||
      !createDoctor.drug_preference ||
      !createDoctor.decision_making ||
      !createDoctor.speciality
    ) {
      openPopup(<FillDataPopup />);
    } else {
      const doctor = {
        name: createDoctor.name,
        avatar_type: createDoctor.avatar_type,
        gender_type: createDoctor.gender_type,
        main_type: createDoctor.main_type,
        experience: createDoctor.experience,
        medical_type: createDoctor.medical_type,
        priorities: createDoctor.priorities,
        intensity: createDoctor.intensity,
        drug_preference: createDoctor.drug_preference,
        decision_making: createDoctor.decision_making,
        speciality: createDoctor.speciality,
        backstory: createDoctor.backstory || "",
      };
      if (createDoctor.activity) doctor.activity = createDoctor.activity;

      const resultAction = await dispatch(postCreateDoctor({doctor}));

      if (postCreateDoctor.fulfilled.match(resultAction)) {
        openPopup(<SuccessfulSavePopup title={t("popup.save_data.doctor")} />);
        dispatch(clearCreateDoctor());
      } else {
        dispatch(setError("Save error!"));
      }
    }
  };

  const genderOptions = [
    {
      label: (
        <div
          className={`${styles.doctorContentCover} ${
            createDoctor?.gender_type === "male"
              ? styles.doctorContentCoverActive
              : ""
          }`}
        >
          <img
            src={maleImg}
            alt={"male"}
          />

          <RootDesc>
            <b
              dangerouslySetInnerHTML={{__html: t("page.doctor.field.male")}}
            />
          </RootDesc>
        </div>
      ),
      value: "male",
    },
    {
      label: (
        <div
          className={`${styles.doctorContentCover} ${
            createDoctor?.gender_type === "female"
              ? styles.doctorContentCoverActive
              : ""
          }`}
        >
          <img
            src={femaleImg}
            alt={"female"}
          />

          <RootDesc>
            <b
              dangerouslySetInnerHTML={{
                __html: t("page.doctor.field.female"),
              }}
            />
          </RootDesc>
        </div>
      ),
      value: "female",
    },
  ];

  const mainTypeOptions = DOCTOR_ENUMS.main_type.map((value) => ({
    label: t(`page.doctor.radio.main_type.${value}.title`),
    value,
  }));

  const experienceOptions = DOCTOR_ENUMS.experience.map((value) => ({
    label: t(`page.doctor.radio.experience.${value}`),
    value,
  }));

  const medicalTypeOptions = DOCTOR_ENUMS.medical_type.map((type, id) => ({
    id,
    name: t(`page.doctor.radio.medical_type.${type}`),
    type,
  }));

  const prioritiesOptions = DOCTOR_ENUMS.priorities.map((value) => ({
    label: t(`page.doctor.radio.priorities.${value}`),
    value,
  }));

  const intensityOptions = DOCTOR_ENUMS.intensity.map((value) => ({
    label: t(`page.doctor.radio.intensity.${value}`),
    value,
  }));

  const activityOptions = DOCTOR_ENUMS.activity.map((value) => ({
    label: t(`page.doctor.radio.activity.${value}`),
    value,
  }));

  const specialityOptions = DOCTOR_ENUMS.speciality.map((value, id) => ({
    id,
    name: t(`page.doctor.radio.speciality.${value}`),
    value,
  }));

  const drugPreferenceOptions = DOCTOR_ENUMS.drug_preference.map((value) => ({
    label: t(`page.doctor.radio.drug_preference.${value}`),
    value,
  }));

  const decisionMakingOptions = DOCTOR_ENUMS.decision_making.map((value) => ({
    label: t(`page.doctor.radio.decision_making.${value}`),
    value,
  }));

  useEffect(() => {
    dispatch(setIsDoctorCreate(!isEqual(createDoctor, initialCreateDoctor)));
  }, [createDoctor, dispatch]);

  useEffect(() => {
    if (doctorRef.current && isMobile) {
      doctorRef.current.scrollIntoView({behavior: "smooth"});
    }
  }, [location]);

  return (
    <div
      ref={doctorRef}
      className={styles.doctorWrapper}
    >
      <BorderArticle>
        <div className={styles.doctor}>
          <BackButton />

          {serverStatus === "loading" ? (
            <Spinner
              width={80}
              height={80}
            />
          ) : (
            <div className={styles.doctorContent}>
              <div className={styles.doctorContentHeadWrapper}>
                <div className={styles.doctorEditContent}>
                  <div className={styles.doctorContentHead}>
                    <div className={"flex items-center gap-[16rem]"}>
                      <EditInput
                        placeholder={t("input.doctor_name_placeholder")}
                        width={!isMobile ? 380 : 210}
                        label={"doctor-name"}
                        type={"text"}
                        value={createDoctor?.name}
                        onChange={(e) =>
                          updateDoctorField("name", e.target.value)
                        }
                      />

                      <ControlButton
                        color={"var(--green)"}
                        txt={t("button.save")}
                        onClick={handleClickSave}
                      />
                    </div>

                    <div className={"flex gap-[16rem]"}>
                      <RootDesc>
                        <b
                          className={"block w-[200rem]"}
                          dangerouslySetInnerHTML={{
                            __html: `${t("page.doctor.field.covers")}*`,
                          }}
                        />
                      </RootDesc>

                      <RadioGroup
                        className={`${styles.doctorContentCovers} ${styles.radioGroupRow}`}
                        options={genderOptions}
                        value={createDoctor?.gender_type}
                        onChange={(option) => {
                          dispatch(setIsChanged(true));
                          dispatch(
                            setCreateDoctor({
                              ...createDoctor,
                              gender_type: option.value,
                              avatar_type:
                                option.value === "male"
                                  ? "male_60"
                                  : "female_40",
                            }),
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <ul className={`${styles.doctorContentBody} ${styles.editBody}`}>
                <EditField
                  label={`${t("page.doctor.field.main_type")}*`}
                  type={"custom"}
                  customContent={
                    <div className={styles.doctorContentMainType}>
                      <RadioGroup
                        className={styles.radioGroup}
                        options={mainTypeOptions}
                        value={createDoctor?.main_type}
                        onChange={(option) =>
                          updateDoctorField("main_type", option.value)
                        }
                      />

                      {createDoctor?.main_type !== "" && (
                        <DoctorContentMainType
                          value={createDoctor?.main_type}
                        />
                      )}
                    </div>
                  }
                />

                <EditField
                  label={`${t("page.doctor.field.experience")}*`}
                  type={"custom"}
                  customContent={
                    <RadioGroup
                      className={`${styles.radioGroup} ${styles.radioGroupRow}`}
                      options={experienceOptions}
                      value={createDoctor?.experience}
                      onChange={(option) =>
                        updateDoctorField("experience", option.value)
                      }
                    />
                  }
                />

                <EditField
                  label={`${t("page.doctor.field.medical_type")}*`}
                  type={"select"}
                  height={150}
                  activeOption={
                    medicalTypeOptions.find(
                      (o) => o.type === createDoctor?.medical_type,
                    )?.name
                  }
                  options={medicalTypeOptions}
                  onSelect={(selectedOption) =>
                    updateDoctorField("medical_type", selectedOption?.type)
                  }
                />

                <EditField
                  label={`${t("page.doctor.field.priorities")}*`}
                  type={"custom"}
                  customContent={
                    <RadioGroup
                      className={`${styles.radioGroup} ${styles.radioGroupRow}`}
                      options={prioritiesOptions}
                      value={createDoctor?.priorities}
                      onChange={(option) =>
                        updateDoctorField("priorities", option.value)
                      }
                    />
                  }
                />

                <EditField
                  label={`${t("page.doctor.field.intensity")}*`}
                  type={"custom"}
                  customContent={
                    <RadioGroup
                      className={`${styles.radioGroup} ${styles.radioGroupRow}`}
                      options={intensityOptions}
                      value={createDoctor?.intensity}
                      onChange={(option) =>
                        updateDoctorField("intensity", option.value)
                      }
                    />
                  }
                />

                <EditField
                  label={t("page.doctor.field.activity")}
                  type={"custom"}
                  customContent={
                    <RadioGroup
                      className={`${styles.radioGroup} ${styles.radioGroupRow}`}
                      options={activityOptions}
                      value={createDoctor?.activity}
                      onChange={(option) =>
                        updateDoctorField("activity", option.value)
                      }
                    />
                  }
                />

                <EditField
                  label={`${t("page.doctor.field.drug_preference")}*`}
                  type={"custom"}
                  customContent={
                    <RadioGroup
                      className={`${styles.radioGroup} ${styles.radioGroupRow}`}
                      options={drugPreferenceOptions}
                      value={createDoctor?.drug_preference}
                      onChange={(option) =>
                        updateDoctorField("drug_preference", option.value)
                      }
                    />
                  }
                />

                <EditField
                  label={`${t("page.doctor.field.decision_making")}*`}
                  type={"custom"}
                  customContent={
                    <RadioGroup
                      className={`${styles.radioGroup} ${styles.radioGroupRow}`}
                      options={decisionMakingOptions}
                      value={createDoctor?.decision_making}
                      onChange={(option) =>
                        updateDoctorField("decision_making", option.value)
                      }
                    />
                  }
                />

                <EditField
                  label={`${t("page.doctor.field.speciality")}*`}
                  type={"select"}
                  height={150}
                  activeOption={
                    specialityOptions.find(
                      (o) => o.value === createDoctor?.speciality,
                    )?.name
                  }
                  options={specialityOptions}
                  onSelect={(selectedOption) =>
                    updateDoctorField("speciality", selectedOption?.value)
                  }
                />

                <EditField
                  label={t("page.doctor.field.backstory")}
                  type={"textarea"}
                  value={createDoctor?.backstory}
                  onChange={(e) =>
                    updateDoctorField("backstory", e.target.value)
                  }
                />
              </ul>
            </div>
          )}
        </div>
      </BorderArticle>
    </div>
  );
};
